import { useMemo } from 'react';
import { ErrorLine } from '../components/ErrorLine';
import { FeatureCarousel } from '../components/FeatureCarousel';
import { GameBanner } from '../components/GameBanner';
import { PageBanner } from '../components/PageBanner';
import { GameCard } from '../components/GameCard';
import { SectionHead } from '../components/SectionHead';
import { SkeletonGrid } from '../components/Skeleton';
import { isFinal, isLive, type Game, type League } from '../lib/api';
import { byImportance, headline } from '../lib/importance';
import { useGames } from '../lib/useGames';

function group(games: Game[]) {
  const live = games.filter(isLive);
  // Upcoming used to sort by kickoff, which quietly threw away the ranking the
  // caller had just applied: "Ranked matchups first" sat above Western Kentucky
  // at New Mexico State because they happened to play on the Thursday.
  const upcoming = byImportance(games.filter((g) => !isLive(g) && !isFinal(g)));
  const finals = games.filter(isFinal).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  return { live, upcoming, finals };
}

function Grid({ title, games }: { title: string; games: Game[] }) {
  if (!games.length) return null;
  return (
    <div className="mb-10">
      <div className="mb-3 flex items-baseline gap-3 border-b-2 border-ink pb-1.5">
        <p className="kicker !text-ink">{title}</p>
        <span className="num text-[13px] text-muted">{games.length}</span>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">{games.map((g) => <GameCard key={g.id} g={g} />)}</div>
    </div>
  );
}

export function LeaguePage({ league }: { league: League }) {
  const { games, loading, error, reload } = useGames(league);
  const { live, upcoming, finals } = useMemo(() => group(games), [games]);
  // The same rotating banner as the home page, but only this league's games.
  // These stay in the grid below as well: this page is the schedule, and a week
  // with games missing from it because they were highlighted reads as a bug.
  const featured = useMemo(() => headline(games, 5), [games]);
  return (
    <>
      {/* The banner is the top of the page and has to be there even when the feed
          has nothing, which happens between the last whistle of a week and the
          scoreboard rolling over, and whenever the API is unreachable. */}
      {featured.length > 0 ? (
        <FeatureCarousel games={featured} render={(g) => <GameBanner g={g} />} />
      ) : (
        <PageBanner fill="none" style={{ background: '#14161a' }}>
          <p className="kicker !text-paper/70">{league === 'nfl' ? 'NFL' : 'College football'}</p>
          <h1 className="display mt-1.5 text-[34px] leading-[0.95] md:text-[44px]">
            {loading ? 'Loading this week.' : 'Between weeks.'}
          </h1>
          <p className="mt-2.5 max-w-[48ch] text-[15px] text-paper/70">
            {loading ? 'Pulling the scoreboard.' : 'The next slate appears here as soon as it is posted.'}
          </p>
        </PageBanner>
      )}
      <section className="section">
      <div className="container">
        <SectionHead title={league === 'nfl' ? 'NFL this week.' : 'College this week.'} eyebrow={league === 'nfl' ? 'NFL' : 'FBS'} sub={league === 'cfb' ? 'Ranked matchups first, closest first.' : 'The games worth watching first.'} />
        {error && <ErrorLine message="Could not load the schedule." onRetry={reload} />}
        {loading && !games.length && <SkeletonGrid n={8} />}
        {!loading && !error && !games.length && <p className="body-muted">No games on the schedule right now.</p>}
        <Grid title="Live" games={live} />
        <Grid title="Upcoming" games={upcoming} />
        <Grid title="Final" games={finals} />
      </div>
      </section>
    </>
  );
}
